import { createReducer, on } from "@ngrx/store";
import { ValorationActions } from "./valoration.action.types";
import { saveAssessmentSummary } from "./valoration.actions";
import { SCORES_SUMS } from "@app/constants";
import { Overview } from "@app/interface/assessment";
import * as PRAGMAPOWERS from '../../utils/mocks/valoration.mock';
import { PragmaPowerCategory } from "@app/interface/pragma-power-categories.interface";
import { PragmaPowerDefinition } from "@app/interface/pragma-power-definition.interface";
import { PragmaPowerScores } from "@app/interface/pragma-power-scores-interface";
import { AssessmentSummary } from "@app/interface/evaluator-valoration-submenu";
import { Valoration } from "@app/interface/valoration-last-assessment.interface";

export const initialPragmaPowersCategories: PragmaPowerCategory[] = PRAGMAPOWERS.PRAGMAPOWERS_CATEGORIES_INIT;
export const initialPragmaPowerDefinition: PragmaPowerDefinition[] = PRAGMAPOWERS.PRAGMAPOWER_DEFINITION_INIT;
export const initialPragmaPowerScores: PragmaPowerScores[] = [];
export const initialCurrentValoration: Valoration = PRAGMAPOWERS.mockInitialCurrentValoration;

export interface HomeValorationState {
  pragmaPowerCategories: PragmaPowerCategory[],
  pragmaPowerDefinition: PragmaPowerDefinition[],
  pragmaPowerScores: PragmaPowerScores[],
  currentValoration: Valoration,
  lastValoration: Valoration | undefined,
  approvedAssessment: Overview | null | undefined,
  rejectedAssessment: Overview | null | undefined,
  pendingAssessment: Overview | null | undefined,
  startedAssessment: Overview | null | undefined,
  finalizedAutoassessment: Overview | null | undefined,
  startedAutoassessment: Overview | null | undefined
}

export const initialHomeAutoValoration: HomeValorationState =  {
  pragmaPowerCategories: initialPragmaPowersCategories,
  pragmaPowerDefinition: initialPragmaPowerDefinition,
  pragmaPowerScores: initialPragmaPowerScores,
  currentValoration: initialCurrentValoration,
  lastValoration: {} as Valoration,
  approvedAssessment: undefined,
  rejectedAssessment: undefined,
  pendingAssessment: undefined,
  startedAssessment: undefined,
  finalizedAutoassessment: undefined,
  startedAutoassessment: undefined
}

export const initialState: AssessmentSummary = {
  isTeamValued: false,
  isTechnicalValued: false,
  canSeniorityUp: false,
  isLowerLastAssessmentScore: false,
  previousAssessmentScore: SCORES_SUMS.zero,
};

export const HomeValorationReducer = createReducer(
  initialHomeAutoValoration,
  on(ValorationActions.loadPragmaPowerCategoriesSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      pragmaPowerCategories: action.pragmaPowerCategories
    })
  ),
  on(ValorationActions.loadPragmaPowerDefinitionSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      pragmaPowerDefinition: [...state.pragmaPowerDefinition, ...action.pragmaPowerDefinition]
    })
  ),
  on(ValorationActions.loadPragmaPowerScoresSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      pragmaPowerScores: action.pragmaPowerScores
    })
  ),
  on(ValorationActions.loadCurrentValoration, 
    (state, action): HomeValorationState => ({
      ...state,
      currentValoration: action.currentValoration
    })
  ), 
  on(ValorationActions.loadLastValorationSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      lastValoration: action.lastValoration
    })
  ),
  on(ValorationActions.loadApprovedAssessmentSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      approvedAssessment: action.approvedAssessment
    })
  ),
  on(ValorationActions.loadRejectedAssessmentSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      rejectedAssessment: action.rejectedAssessment
    })
  ),
  on(ValorationActions.loadPendingAssessmentSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      pendingAssessment: action.pendingAssessment
    })
  ),
  on(ValorationActions.loadStartedAssessmentSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      startedAssessment: action.startedAssessment
    })
  ),
  on(ValorationActions.loadFinalizedAutoassessmentSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      finalizedAutoassessment: action.finalizedAutoassessment
    })
  ),
  on(ValorationActions.loadStartedAutoassessmentSuccess, 
    (state, action): HomeValorationState => ({
      ...state,
      startedAutoassessment: action.startedAutoassessment
    })
  ),
  on(ValorationActions.setSeniorityCurrentValoration, (state, action): HomeValorationState => {
    const updatedDataValoration = [...(state.currentValoration?.data_valoration || [])];
    const index = updatedDataValoration.findIndex(
      s => s.seniority_id === action.seniority.seniority_id
    );
  
    if (index !== -1) {
      updatedDataValoration[index] = action.seniority;
    }
  
    return {
      ...state,
      currentValoration: {
        ...state.currentValoration,
        data_valoration: updatedDataValoration
      } as Valoration
    };
  }),
  on(ValorationActions.resetFinalizedAssessment, (state) => ({
    ...state,
    finalizedAssessment: null
  })),
  on(ValorationActions.resetApprovedAssessment, (state) => ({
    ...state,
    approvedAssessment: null
  })),
  on(ValorationActions.resetRejectedAssessment, (state) => ({
    ...state,
    rejectedAssessment: null
  })),
  on(ValorationActions.resetStartedAssessment, (state) => ({
    ...state,
    startedAssessment: null
  })),
  on(ValorationActions.resetPendingAssessment, (state) => ({
    ...state,
    pendingAssessment: null
  })),
  on(ValorationActions.resetFinalizedAutoassessment, (state) => ({
    ...state,
    finalizedAutoassessment: null
  })),
  on(ValorationActions.resetStartedAutoassessment, (state) => ({
    ...state,
    startedAutoassessment: null
  })),
  on(ValorationActions.resetValorationState, () => initialHomeAutoValoration)
);

export const assessmentReducer = createReducer(
  initialState,
  on(saveAssessmentSummary, (state, { isTeamValued, isTechnicalValued, canSeniorityUp, previousAssessmentScore, isLowerLastAssessmentScore }) => ({
    ...state,
    isTeamValued,
    isTechnicalValued,
    canSeniorityUp,
    previousAssessmentScore,
    isLowerLastAssessmentScore
  }))
);
